export interface GeoNode {
  id: number;
  name: string;
}

/**
 * The states a geo scope applies to. A single-state deployment always means
 * that one state (there is nothing to pick), so it can never be "cleared" by
 * resetting the user's selection; with several states it is whatever the
 * admin selected.
 */
export const getEffectiveStateNames = (states: GeoNode[], selectedStateNames: string[]): string[] =>
  states.length === 1 ? [states[0].name] : selectedStateNames;

const matchesKey = (node: GeoNode, keys: string[]) => keys.includes(node.name) || keys.includes(String(node.id));

/**
 * Finds which state owns any of the given districts (matched by name or id),
 * by checking each state's district list in turn. Used when restoring a saved
 * scope that has districts but no explicit STATE row, so the owning state is
 * looked up rather than assumed. Resolves to null when no state matches.
 */
export const findStateOwningDistricts = async <S extends GeoNode>(
  states: S[],
  districtKeys: string[],
  fetchDistricts: (stateId: number) => Promise<GeoNode[]>
): Promise<S | null> => {
  if (districtKeys.length === 0) return null;
  for (const state of states) {
    const districts = await fetchDistricts(state.id);
    if (districts.some((d) => matchesKey(d, districtKeys))) return state;
  }
  return null;
};

export type GeoScopeType = 'STATE' | 'DISTRICT' | 'BLOCK' | 'INSTITUTE';

/**
 * Which scope rows each geo level is allowed to save. Lower levels can hold an
 * auto-selected value (e.g. a state's only district, a district's only block)
 * purely for display, so saving must never include levels the role doesn't use.
 */
export const SCOPE_TYPES_BY_GEO_LEVEL: Record<string, GeoScopeType[]> = {
  state: ['STATE'],
  district: ['DISTRICT'],
  block: ['DISTRICT', 'BLOCK'],
  facility: ['DISTRICT', 'BLOCK'],
  institute: ['INSTITUTE'],
};

export const isScopeTypeSavedForLevel = (level: string | null, scopeType: GeoScopeType): boolean =>
  !!level && (SCOPE_TYPES_BY_GEO_LEVEL[level] || []).includes(scopeType);

export type GeoLevelName = 'state' | 'district' | 'block' | 'facility' | 'institute';

/** The geographic level a role covers, read from its code (STATE_…, DISTRICT_…, BLOCK_…, FACILITY_…, INSTITUTE_…). */
export const geoLevelFromRoleCode = (code?: string): GeoLevelName | null => {
  if (!code) return null;
  const c = code.toUpperCase();
  if (c.includes('INSTITUTE')) return 'institute';
  if (c.includes('BLOCK')) return 'block';
  if (c.includes('DISTRICT')) return 'district';
  if (c.includes('STATE')) return 'state';
  if (c.includes('FACILITY')) return 'facility';
  return null;
};
